import { useState } from 'react'
import { Trash2 } from 'lucide-react'
import Button from '@/components/button'
import ConfirmDialog from '@/components/confirm-dialog'
import EmptyState from '@/components/empty-state'
import LookupSelect from '@/components/lookup-select'
import { Radio } from '@/components/radio'
import Skeleton from '@/components/skeleton'
import { useDefinirPermissoesMutation } from '@/http/mutations/credenciais'
import { usePermissoesQuery } from '@/http/queries/credenciais'
import {
  useUnidadesDisponiveisLookupQuery,
  useUsuariosDisponiveisLookupQuery,
} from '@/http/queries/lookups'
import type { Credencial, TipoPermissao } from '@/types/credencial'
import type { LookupItem, PermissaoOption } from '@/types/lookup'
import { useAuth } from '@/utility/context/auth-context'
import { ACAO } from '@/utility/permissions'
import { useDebouncedValue } from '@/utility/use-debounced-value'
import styles from '../style.module.css'

interface Permissoes {
  usuarios: PermissaoOption[]
  unidades: PermissaoOption[]
}

const PERMISSAO_MODO_OPTIONS: { value: TipoPermissao; label: string; hint: string }[] = [
  {
    value: 'Nenhum',
    label: 'Sem restrição',
    hint: 'Qualquer usuário ou unidade desse cliente pode usar essa credencial.',
  },
  {
    value: 'Usuario',
    label: 'Restringir por usuários',
    hint: 'Só os usuários selecionados abaixo podem usar essa credencial.',
  },
  {
    value: 'Unidade',
    label: 'Restringir por unidades',
    hint: 'Só as unidades selecionadas abaixo podem usar essa credencial.',
  },
]

const paraLookupItemPermissao = (opcao: PermissaoOption): LookupItem => ({
  id: opcao.value,
  rotulo: opcao.label,
  detalhe: null,
})

const PERMISSAO_PONTO_ATENDIMENTO = {
  label: 'Restringir por pontos de atendimento',
  hint: 'Restrição antiga, mantida só para leitura. Escolher uma das opções acima a substitui.',
}

const PermissoesTab = ({ credencial }: { credencial: Credencial }) => {
  const credencialId = credencial.id ?? ''
  const permissoesQuery = usePermissoesQuery(credencialId || undefined)

  if (permissoesQuery.isPending) {
    return <Skeleton variant="text" count={4} />
  }

  if (!permissoesQuery.data) {
    return <EmptyState title="Não foi possível carregar as permissões" />
  }

  return (
    <PermissoesEditor
      credencial={credencial}
      credencialId={credencialId}
      permissoes={permissoesQuery.data}
    />
  )
}

const PermissoesEditor = ({
  credencial,
  credencialId,
  permissoes,
}: {
  credencial: Credencial
  credencialId: string
  permissoes: Permissoes
}) => {
  const { can } = useAuth()
  const podeEditar = can(ACAO.EditarCredencial)

  const [modo, setModo] = useState<TipoPermissao>(credencial.tipoPermissao)
  const [usuarios, setUsuarios] = useState<PermissaoOption[]>(permissoes.usuarios)
  const [unidades, setUnidades] = useState<PermissaoOption[]>(permissoes.unidades)
  const [alterado, setAlterado] = useState(false)
  const [confirmando, setConfirmando] = useState(false)

  const definir = useDefinirPermissoesMutation(credencialId, () => {
    setConfirmando(false)
    setAlterado(false)
  })

  const [termoUsuario, setTermoUsuario] = useState('')
  const [usuarioEscolhido, setUsuarioEscolhido] = useState<LookupItem | null>(null)
  const termoUsuarioDebounced = useDebouncedValue(termoUsuario)
  const usuariosDisponiveis = useUsuariosDisponiveisLookupQuery(
    credencialId,
    termoUsuarioDebounced,
    modo === 'Usuario',
  )
  const opcoesUsuarioDisponivel: LookupItem[] = (usuariosDisponiveis.data ?? [])
    .filter((item) => !usuarios.some((escolhido) => escolhido.value === item.value))
    .map(paraLookupItemPermissao)

  const [termoUnidade, setTermoUnidade] = useState('')
  const [unidadeEscolhida, setUnidadeEscolhida] = useState<LookupItem | null>(null)
  const termoUnidadeDebounced = useDebouncedValue(termoUnidade)
  const unidadesDisponiveis = useUnidadesDisponiveisLookupQuery(
    credencialId,
    termoUnidadeDebounced,
    modo === 'Unidade',
  )
  const opcoesUnidadeDisponivel: LookupItem[] = (unidadesDisponiveis.data ?? [])
    .filter((item) => !unidades.some((escolhido) => escolhido.value === item.value))
    .map(paraLookupItemPermissao)

  const trocarModo = (novoModo: TipoPermissao) => {
    setModo(novoModo)
    setAlterado(true)
  }

  const adicionarUsuario = () => {
    if (!usuarioEscolhido) return
    setUsuarios((atual) => [...atual, { value: usuarioEscolhido.id, label: usuarioEscolhido.rotulo }])
    setUsuarioEscolhido(null)
    setTermoUsuario('')
    setAlterado(true)
  }

  const removerUsuario = (value: string) => {
    setUsuarios((atual) => atual.filter((item) => item.value !== value))
    setAlterado(true)
  }

  const adicionarUnidade = () => {
    if (!unidadeEscolhida) return
    setUnidades((atual) => [...atual, { value: unidadeEscolhida.id, label: unidadeEscolhida.rotulo }])
    setUnidadeEscolhida(null)
    setTermoUnidade('')
    setAlterado(true)
  }

  const removerUnidade = (value: string) => {
    setUnidades((atual) => atual.filter((item) => item.value !== value))
    setAlterado(true)
  }

  const faltaEscolher =
    (modo === 'Usuario' && usuarios.length === 0) || (modo === 'Unidade' && unidades.length === 0)

  const onConfirmar = () => {
    definir.mutate({
      tipoPermissao: modo,
      usuarios: modo === 'Usuario' ? usuarios.map((item) => item.value) : [],
      unidades: modo === 'Unidade' ? unidades.map((item) => item.value) : [],
      pontosAtendimento: [],
    })
  }

  return (
    <>
      <div className={styles.sections}>
        <fieldset className={styles.radioGroup} disabled={!podeEditar}>
          <legend className="sr-only">Quem pode usar essa credencial</legend>
          {PERMISSAO_MODO_OPTIONS.map((opcao) => (
            <Radio
              key={opcao.value}
              name="modoPermissao"
              label={opcao.label}
              hint={opcao.hint}
              checked={modo === opcao.value}
              onChange={() => trocarModo(opcao.value)}
            />
          ))}
          {credencial.tipoPermissao === 'PontoAtendimento' ? (
            <Radio
              name="modoPermissao"
              label={PERMISSAO_PONTO_ATENDIMENTO.label}
              hint={PERMISSAO_PONTO_ATENDIMENTO.hint}
              checked={modo === 'PontoAtendimento'}
              disabled
              readOnly
            />
          ) : null}
        </fieldset>

        {modo === 'Usuario' ? (
          <div className={styles.itemBuilder}>
            {podeEditar ? (
              <div className={styles.itemBuilderRow}>
                <LookupSelect
                  id="buscaUsuarioPermissao"
                  termo={termoUsuario}
                  onTermoChange={setTermoUsuario}
                  itens={opcoesUsuarioDisponivel}
                  isLoading={usuariosDisponiveis.isFetching}
                  mostrarOpcoesSemBusca
                  selecionado={usuarioEscolhido}
                  placeholder="Preencha o nome do usuário"
                  onSelecionar={setUsuarioEscolhido}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!usuarioEscolhido}
                  onClick={adicionarUsuario}
                >
                  Adicionar
                </Button>
              </div>
            ) : null}

            {usuarios.length ? (
              <ul className={styles.itemList}>
                {usuarios.map((item) => (
                  <li key={item.value} className={styles.itemRow}>
                    <span>{item.label}</span>
                    {podeEditar ? (
                      <button
                        type="button"
                        className={styles.itemRemover}
                        aria-label={`Remover ${item.label}`}
                        onClick={() => removerUsuario(item.value)}
                      >
                        <Trash2 size={15} />
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState
                title="Nenhum usuário selecionado ainda"
                description="Escolha pelo menos um usuário para poder salvar a restrição."
              />
            )}
          </div>
        ) : null}

        {modo === 'Unidade' ? (
          <div className={styles.itemBuilder}>
            {podeEditar ? (
              <div className={styles.itemBuilderRow}>
                <LookupSelect
                  id="buscaUnidadePermissao"
                  termo={termoUnidade}
                  onTermoChange={setTermoUnidade}
                  itens={opcoesUnidadeDisponivel}
                  isLoading={unidadesDisponiveis.isFetching}
                  mostrarOpcoesSemBusca
                  selecionado={unidadeEscolhida}
                  placeholder="Preencha o nome da unidade"
                  onSelecionar={setUnidadeEscolhida}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!unidadeEscolhida}
                  onClick={adicionarUnidade}
                >
                  Adicionar
                </Button>
              </div>
            ) : null}

            {unidades.length ? (
              <ul className={styles.itemList}>
                {unidades.map((item) => (
                  <li key={item.value} className={styles.itemRow}>
                    <span>{item.label}</span>
                    {podeEditar ? (
                      <button
                        type="button"
                        className={styles.itemRemover}
                        aria-label={`Remover ${item.label}`}
                        onClick={() => removerUnidade(item.value)}
                      >
                        <Trash2 size={15} />
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState
                title="Nenhuma unidade selecionada ainda"
                description="Escolha pelo menos uma unidade para poder salvar a restrição."
              />
            )}
          </div>
        ) : null}

        {modo === 'Nenhum' ? (
          <EmptyState
            title="Sem restrição de uso"
            description="Qualquer usuário ou unidade desse cliente pode usar essa credencial."
          />
        ) : null}

        {modo === 'PontoAtendimento' ? (
          <EmptyState
            title="Restrita por pontos de atendimento"
            description="Esses vínculos não são editáveis por aqui. Escolha uma das opções acima para substituir a restrição."
          />
        ) : null}

        {podeEditar ? (
          <div className={styles.actions}>
            <Button
              type="button"
              disabled={!alterado || faltaEscolher || modo === 'PontoAtendimento'}
              onClick={() => setConfirmando(true)}
            >
              Salvar alterações
            </Button>
          </div>
        ) : null}
      </div>

      <ConfirmDialog
        isOpen={confirmando}
        toggle={() => setConfirmando(false)}
        title="Confirmar alteração de permissões"
        message="Essa alteração muda quem pode usar essa credencial, com efeito imediato. Deseja continuar?"
        confirmLabel="Salvar alterações"
        tone="warning"
        isLoading={definir.isPending}
        onConfirm={onConfirmar}
      />
    </>
  )
}

export default PermissoesTab
